import { useMemo, useState } from 'react';
import { SearchParamsValue } from '@apps/stays/components/SearchParamsObserver/SearchParamsObserver';
import { formatDateComponentsToString } from '@shared/utils/dateHelpers';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

/**
 * Custom hook to handle URL parameters for hotel booking redirects
 * Manages search params state and generates redirect overrides for dates and occupancy
 */
export const useHotelBookingRedirectOverrides = () => {
  const [searchParams, setSearchParams] = useState<SearchParamsValue | null>(null);

  const handleSearchParamsUpdate = useEventCallback((value: SearchParamsValue) => {
    setSearchParams(value);
  });

  const redirectOverrides = useMemo(() => {
    if (!searchParams) return undefined;
    return {
      dates:
        searchParams.startDate.valid && searchParams.endDate.valid
          ? {
              checkIn: formatDateComponentsToString(searchParams.startDate.value),
              checkOut: formatDateComponentsToString(searchParams.endDate.value)
            }
          : undefined,
      occupancies: searchParams.rooms.valid
        ? {
            occupancies: searchParams.rooms.value.map(room => ({
              adults: room.adults,
              childrenAges: room.childrenAges
            })),
            unitCount: searchParams.rooms.value.length
          }
        : undefined
    };
  }, [searchParams]);

  return {
    searchParams,
    handleSearchParamsUpdate,
    redirectOverrides
  };
};
